/**
 * Ripple Shader Effect Manager
 * 
 * Manages WebGL2 shader that creates ripple effects on the UI canvas.
 * Triggers ripples on Ctrl+Click.
 */

import { RIPPLE_VERTEX_SHADER, RIPPLE_FRAGMENT_SHADER, PASSTHROUGH_FRAGMENT_SHADER, MAX_RIPPLES, RIPPLE_LIFETIME } from './ripple.js';

export interface RippleEffectState {
  active: boolean;
  gl: WebGL2RenderingContext | null;
  rippleProgram: WebGLProgram | null;
  passthroughProgram: WebGLProgram | null;
  vao: WebGLVertexArrayObject | null;
  texture: WebGLTexture | null;
  uniformLocations: {
    u_resolution: WebGLUniformLocation | null;
    u_time: WebGLUniformLocation | null;
    u_rippleCount: WebGLUniformLocation | null;
    u_centers: WebGLUniformLocation | null;
    u_starts: WebGLUniformLocation | null;
    u_amplitudes: WebGLUniformLocation | null;
    u_bulgeCenter: WebGLUniformLocation | null;
    u_bulgeActive: WebGLUniformLocation | null;
    u_sourceTexture: WebGLUniformLocation | null;
  };
  passthroughUniformLocations: {
    u_resolution: WebGLUniformLocation | null;
    u_sourceTexture: WebGLUniformLocation | null;
  };
  centers: Float32Array;
  starts: Float32Array;
  amplitudes: Float32Array; // Per-ripple amplitude scale (0-1)
  rippleCount: number;
  bulgeCenter: [number, number]; // Current bulge position
  bulgeActive: boolean; // Whether bulge is currently active
}

/**
 * Create ripple effect state
 */
export function createRippleEffect(canvas: HTMLCanvasElement): RippleEffectState | null {
  const gl = canvas.getContext('webgl2', {
    antialias: false, // Disable WebGL antialiasing - we want pixel-perfect rendering
    alpha: true, // Enable alpha channel
    premultipliedAlpha: true, // Use premultiplied alpha to fix dark outlines and white burn
    preserveDrawingBuffer: false,
    desynchronized: false // Ensure proper synchronization for color accuracy
  });
  if (!gl) {
    return null;
  }

  // Compile shader (gl is guaranteed non-null here)
  function compileShader(glContext: WebGL2RenderingContext, type: number, source: string): WebGLShader | null {
    const shader = glContext.createShader(type);
    if (!shader) return null;

    glContext.shaderSource(shader, source);
    glContext.compileShader(shader);

    if (!glContext.getShaderParameter(shader, glContext.COMPILE_STATUS)) {
      const log = glContext.getShaderInfoLog(shader);
      glContext.deleteShader(shader);
      return null;
    }

    return shader;
  }

  // Create ripple program
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, RIPPLE_VERTEX_SHADER);
  const rippleFragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, RIPPLE_FRAGMENT_SHADER);
  const passthroughFragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, PASSTHROUGH_FRAGMENT_SHADER);

  if (!vertexShader || !rippleFragmentShader || !passthroughFragmentShader) {
    return null;
  }

  const rippleProgram = gl.createProgram();
  if (!rippleProgram) return null;
  gl.attachShader(rippleProgram, vertexShader);
  gl.attachShader(rippleProgram, rippleFragmentShader);
  gl.linkProgram(rippleProgram);
  if (!gl.getProgramParameter(rippleProgram, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(rippleProgram);
    gl.deleteProgram(rippleProgram);
    return null;
  }

  const passthroughProgram = gl.createProgram();
  if (!passthroughProgram) return null;
  gl.attachShader(passthroughProgram, vertexShader);
  gl.attachShader(passthroughProgram, passthroughFragmentShader);
  gl.linkProgram(passthroughProgram);
  if (!gl.getProgramParameter(passthroughProgram, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(passthroughProgram);
    gl.deleteProgram(passthroughProgram);
    return null;
  }

  // Create VAO
  const vao = gl.createVertexArray();
  gl.bindVertexArray(vao);

  // Get uniform locations for ripple program
  gl.useProgram(rippleProgram);
  const uniformLocations = {
    u_resolution: gl.getUniformLocation(rippleProgram, 'u_resolution'),
    u_time: gl.getUniformLocation(rippleProgram, 'u_time'),
    u_rippleCount: gl.getUniformLocation(rippleProgram, 'u_rippleCount'),
    u_centers: gl.getUniformLocation(rippleProgram, 'u_centers[0]'),
    u_starts: gl.getUniformLocation(rippleProgram, 'u_starts[0]'),
    u_amplitudes: gl.getUniformLocation(rippleProgram, 'u_amplitudes[0]'),
    u_bulgeCenter: gl.getUniformLocation(rippleProgram, 'u_bulgeCenter'),
    u_bulgeActive: gl.getUniformLocation(rippleProgram, 'u_bulgeActive'),
    u_sourceTexture: gl.getUniformLocation(rippleProgram, 'u_sourceTexture'),
  };

  // Get uniform locations for passthrough program
  gl.useProgram(passthroughProgram);
  const passthroughUniformLocations = {
    u_resolution: gl.getUniformLocation(passthroughProgram, 'u_resolution'),
    u_sourceTexture: gl.getUniformLocation(passthroughProgram, 'u_sourceTexture'),
  };

  // Create texture for source UI canvas
  // Use LINEAR filtering for smooth text rendering
  const texture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

  const state: RippleEffectState = {
    active: false,
    gl,
    rippleProgram,
    passthroughProgram,
    vao,
    texture,
    uniformLocations,
    passthroughUniformLocations,
    centers: new Float32Array(MAX_RIPPLES * 2),
    starts: new Float32Array(MAX_RIPPLES),
    amplitudes: new Float32Array(MAX_RIPPLES), // Per-ripple intensity (0-1)
    rippleCount: 0,
    bulgeCenter: [0, 0],
    bulgeActive: false,
  };

  return state;
}

/**
 * Add a ripple at the specified position
 * 
 * @param intensity Amplitude scale (0-1). Default 1.0 for user clicks, 0.15 for automatic spawns
 */
export function addRipple(
  state: RippleEffectState,
  x: number,
  y: number,
  time: number,
  intensity: number = 1.0
): void {
  if (!state.gl) return;

  // Clamp intensity to valid range
  const clampedIntensity = Math.max(0, Math.min(1, intensity));

  if (state.rippleCount >= MAX_RIPPLES) {
    // If full, shift array and add to end
    state.centers.copyWithin(0, 2);
    state.starts.copyWithin(0, 1);
    state.amplitudes.copyWithin(0, 1);
    state.centers[(MAX_RIPPLES - 1) * 2] = x;
    state.centers[(MAX_RIPPLES - 1) * 2 + 1] = y;
    state.starts[MAX_RIPPLES - 1] = time;
    state.amplitudes[MAX_RIPPLES - 1] = clampedIntensity;
    return;
  }

  state.centers[state.rippleCount * 2] = x;
  state.centers[state.rippleCount * 2 + 1] = y;
  state.starts[state.rippleCount] = time;
  state.amplitudes[state.rippleCount] = clampedIntensity;
  state.rippleCount++;
}

/**
 * Set bulge position (magnifying glass effect)
 */
export function setBulge(
  state: RippleEffectState,
  x: number,
  y: number,
  active: boolean
): void {
  state.bulgeCenter[0] = x;
  state.bulgeCenter[1] = y;
  state.bulgeActive = active;
  state.active = active || state.rippleCount > 0;
}

/**
 * Update ripple effect - remove expired ripples
 */
export function updateRipples(state: RippleEffectState, currentTime: number): void {
  if (state.rippleCount === 0) return;

  let writeIndex = 0;
  for (let i = 0; i < state.rippleCount; i++) {
    const alive = (currentTime - state.starts[i]) <= RIPPLE_LIFETIME;
    if (alive) {
      if (writeIndex !== i) {
        state.centers[writeIndex * 2] = state.centers[i * 2];
        state.centers[writeIndex * 2 + 1] = state.centers[i * 2 + 1];
        state.starts[writeIndex] = state.starts[i];
      }
      writeIndex++;
    }
  }
  state.rippleCount = writeIndex;
}

/**
 * Update source texture from UI canvas
 */
export function updateSourceTexture(
  state: RippleEffectState,
  sourceCanvas: HTMLCanvasElement
): void {
  if (!state.gl || !state.texture) return;

  const gl = state.gl;

  // Update texture from source canvas
  // Convert Canvas2D (non-premultiplied) to premultiplied alpha on GPU upload
  // This fixes dark outlines and white burn when overlapping
  gl.bindTexture(gl.TEXTURE_2D, state.texture);
  // Convert non-premultiplied Canvas2D pixels to premultiplied during upload
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sourceCanvas);
}

/**
 * Render to canvas using WebGL - always uses the same rendering path
 * Uses ripple shader when ripples are active, pass-through shader otherwise
 */
export function renderRipple(
  state: RippleEffectState,
  width: number,
  height: number,
  time: number
): void {
  if (!state.gl || !state.rippleProgram || !state.passthroughProgram || !state.vao || !state.texture) {
    return;
  }

  const gl = state.gl;

  // Update viewport
  gl.viewport(0, 0, width, height);

  // Clear with transparent background
  // We'll handle edge pixels in the shader
  gl.clearColor(0, 0, 0, 0);
  gl.clear(gl.COLOR_BUFFER_BIT);

  // Enable blending with premultiplied alpha mode
  // This fixes dark outlines and white burn when overlapping
  // UNPACK_PREMULTIPLY_ALPHA_WEBGL converts Canvas2D pixels during upload
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

  gl.bindVertexArray(state.vao);

  // Bind source texture
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, state.texture);

  // Update active state based on ripples and bulge
  state.active = state.rippleCount > 0 || state.bulgeActive;

  // Use ripple shader if ripples are active or bulge is active, otherwise use pass-through
  if (state.rippleCount > 0 || state.bulgeActive) {
    gl.useProgram(state.rippleProgram);
    if (state.uniformLocations.u_sourceTexture) {
      gl.uniform1i(state.uniformLocations.u_sourceTexture, 0);
    }
    if (state.uniformLocations.u_resolution) {
      gl.uniform2f(state.uniformLocations.u_resolution, width, height);
    }
    if (state.uniformLocations.u_time) {
      gl.uniform1f(state.uniformLocations.u_time, time);
    }
    if (state.uniformLocations.u_rippleCount) {
      gl.uniform1i(state.uniformLocations.u_rippleCount, state.rippleCount);
    }
    if (state.uniformLocations.u_centers) {
      gl.uniform2fv(state.uniformLocations.u_centers, state.centers, 0, state.rippleCount * 2);
    }
    if (state.uniformLocations.u_starts) {
      gl.uniform1fv(state.uniformLocations.u_starts, state.starts, 0, state.rippleCount);
    }
    if (state.uniformLocations.u_amplitudes) {
      gl.uniform1fv(state.uniformLocations.u_amplitudes, state.amplitudes, 0, state.rippleCount);
    }
    if (state.uniformLocations.u_bulgeCenter) {
      gl.uniform2f(state.uniformLocations.u_bulgeCenter, state.bulgeCenter[0], state.bulgeCenter[1]);
    }
    if (state.uniformLocations.u_bulgeActive) {
      gl.uniform1f(state.uniformLocations.u_bulgeActive, state.bulgeActive ? 1.0 : 0.0);
    }
  } else {
    // Pass-through shader - exact pixel copy
    gl.useProgram(state.passthroughProgram);
    if (state.passthroughUniformLocations.u_sourceTexture) {
      gl.uniform1i(state.passthroughUniformLocations.u_sourceTexture, 0);
    }
    if (state.passthroughUniformLocations.u_resolution) {
      gl.uniform2f(state.passthroughUniformLocations.u_resolution, width, height);
    }
  }

  // Draw
  gl.drawArrays(gl.TRIANGLES, 0, 3);
}

/**
 * Cleanup ripple effect
 */
function destroyRippleEffect(state: RippleEffectState): void {
  if (state.gl) {
    if (state.rippleProgram) {
      state.gl.deleteProgram(state.rippleProgram);
    }
    if (state.passthroughProgram) {
      state.gl.deleteProgram(state.passthroughProgram);
    }
    if (state.vao) {
      state.gl.deleteVertexArray(state.vao);
    }
    if (state.texture) {
      state.gl.deleteTexture(state.texture);
    }
  }
}

